import { UnscheduledEvent } from '../types';
import { AuditNoteEntry } from './AuditModal';
import { parseFlexibleDate } from '../utils/dateUtils';

/**
 * Computes the difference in minutes between two Date objects (b - a).
 */
function diffMinutes(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / (1000 * 60);
}

/**
 * Checks if a SYSTEM entry with the given FCP Code already exists, or has been
 * tombstoned (deleted) so it must not be re-logged.
 */
function hasSystemEntry(
  entries: AuditNoteEntry[],
  code: string,
  deletedFindings: string[] = [],
): boolean {
  if (deletedFindings.includes(code)) return true;
  return entries.some((n) => n.fcpCode === code && n.alias === 'SYSTEM');
}

interface AuditContext {
  event: UnscheduledEvent;
  existingEntries: AuditNoteEntry[];
  waitingPaperworkDate: string;
  waitingPaperworkTime: string;
  barriers: { id: string; type: string; startTime: string; endTime: string; notes: string }[];
  logDate: string;
  logTime: string;
  /** Finding keys deleted by a reviewer; tombstoned so they are not re-logged. */
  deletedFindings?: string[];
}

/**
 * Rule: Short Ground Time â€” ground time < 120 minutes.
 */
function checkShortGroundTime(ctx: AuditContext): AuditNoteEntry | null {
  if (hasSystemEntry(ctx.existingEntries, 'Short Ground Time', ctx.deletedFindings)) return null;
  // Ground time = the gap between the previous (inbound) flight's scheduled
  // departure and the next scheduled flight's departure. (Previously both reads
  // used nextScheduledFlightTimeZulu, so the gap was always 0 and the rule fired
  // on every event whose next-flight time parsed.)
  const prevFlightDt = parseFlexibleDate(ctx.event.previousFlightScheduledDepartureTimeZulu || '');
  const nextFlightDt = parseFlexibleDate(ctx.event.nextScheduledFlightTimeZulu);
  if (!prevFlightDt || !nextFlightDt) return null;
  const groundMinutes = diffMinutes(prevFlightDt, nextFlightDt);
  if (groundMinutes < 0 || groundMinutes >= 120) return null;
  return {
    id: `auto-sgt-${Date.now()}`,
    date: ctx.logDate,
    time: ctx.logTime,
    alias: 'SYSTEM',
    fcpCode: 'Short Ground Time',
    text: `${Math.round(groundMinutes)} minutes is insufficient time to address any maintenance issues that may arise.`,
  };
}

/**
 * Rule: Long Ground Time Finding â€” deck time > 240 minutes.
 */
function checkLongGroundTime(ctx: AuditContext): AuditNoteEntry | null {
  if (hasSystemEntry(ctx.existingEntries, 'Long Ground Time Finding', ctx.deletedFindings))
    return null;
  const nextFlightDt = parseFlexibleDate(ctx.event.nextScheduledFlightTimeZulu);
  const timeDeclaredDt = parseFlexibleDate(ctx.event.timeDeclaredZulu);
  if (!nextFlightDt || !timeDeclaredDt) return null;
  const deckMinutes = diffMinutes(timeDeclaredDt, nextFlightDt);
  if (deckMinutes <= 240) return null;
  const deckHours = (deckMinutes / 60).toFixed(1);
  return {
    id: `auto-lgtf-${Date.now()}`,
    date: ctx.logDate,
    time: ctx.logTime,
    alias: 'SYSTEM',
    fcpCode: 'Long Ground Time Finding',
    text: `Aircraft was on deck for ${deckHours} hours before defect was discovered.`,
  };
}

/**
 * Rule: Documentation Delay â€” paperwork wait > 30 minutes.
 */
function checkDocumentationDelay(ctx: AuditContext): AuditNoteEntry | null {
  if (hasSystemEntry(ctx.existingEntries, 'Documentation Delay', ctx.deletedFindings)) return null;
  if (!ctx.waitingPaperworkDate || !ctx.waitingPaperworkTime) return null;
  const wpDateTime = `${ctx.waitingPaperworkDate} ${ctx.waitingPaperworkTime}:00`;
  const wpDt = parseFlexibleDate(wpDateTime);
  const ertsDt = parseFlexibleDate(ctx.event.returnToServiceTimeZulu);
  if (!wpDt || !ertsDt) return null;
  const waitMinutes = diffMinutes(wpDt, ertsDt);
  if (waitMinutes <= 30) return null;
  return {
    id: `auto-dd-${Date.now()}`,
    date: ctx.logDate,
    time: ctx.logTime,
    alias: 'SYSTEM',
    fcpCode: 'Documentation Delay',
    text: `Waited ${Math.round(waitMinutes)} minutes for paperwork.`,
  };
}

/**
 * Rule: Carrier Comms Issues â€” time entered minus time declared > 60 minutes.
 */
function checkCarrierCommsIssues(ctx: AuditContext): AuditNoteEntry | null {
  if (hasSystemEntry(ctx.existingEntries, 'Carrier Comms Issues', ctx.deletedFindings)) return null;
  const timeEnteredDt = parseFlexibleDate(ctx.event.creationTime);
  const timeDeclaredDt = parseFlexibleDate(ctx.event.timeDeclaredZulu);
  if (!timeEnteredDt || !timeDeclaredDt) return null;
  const commsDelayMinutes = diffMinutes(timeDeclaredDt, timeEnteredDt);
  if (commsDelayMinutes <= 60) return null;
  return {
    id: `auto-cci-${Date.now()}`,
    date: ctx.logDate,
    time: ctx.logTime,
    alias: 'SYSTEM',
    fcpCode: 'Carrier Comms Issues',
    text: `Carrier did not notify MXO until ${Math.round(commsDelayMinutes)} minutes after event was discovered.`,
  };
}

/**
 * Rule: Barrier duration > 24 hours triggers Parts or MX Practice Improvement.
 */
function checkBarrierDurations(ctx: AuditContext): AuditNoteEntry[] {
  const entries: AuditNoteEntry[] = [];
  for (const barrier of ctx.barriers) {
    const startDt = parseFlexibleDate(barrier.startTime);
    if (!startDt) continue;
    const endDt = barrier.endTime ? parseFlexibleDate(barrier.endTime) : new Date();
    if (!endDt) continue;
    const durationHours = diffMinutes(startDt, endDt) / 60;
    if (durationHours <= 24) continue;
    const barrierKey = `auto-barrier-${barrier.id}`;
    if (ctx.deletedFindings && ctx.deletedFindings.includes(barrierKey)) continue;
    const barrierAlreadyLogged = ctx.existingEntries.some(
      (n) => n.alias === 'SYSTEM' && n.id === barrierKey,
    );
    if (barrierAlreadyLogged) continue;
    const fcpForBarrier = barrier.type === 'Parts' ? 'Parts' : 'MX Practice Improvement';
    const barrierNotes = barrier.notes ? `\n\n${barrier.notes}` : '';
    entries.push({
      id: `auto-barrier-${barrier.id}`,
      date: ctx.logDate,
      time: ctx.logTime,
      alias: 'SYSTEM',
      fcpCode: fcpForBarrier,
      text: `${barrier.type} barrier was open for ${durationHours.toFixed(1)} hours.${barrierNotes}`,
    });
  }
  return entries;
}

/**
 * The tombstone key for a SYSTEM finding: barrier findings key off their entry
 * id (`auto-barrier-{barrierId}`); all other findings key off the FCP code.
 */
export function findingKeyForEntry(entry: AuditNoteEntry): string {
  return entry.id.startsWith('auto-barrier-') ? entry.id : entry.fcpCode;
}

/**
 * Runs all automated audit rules and returns new entries to add.
 */
export function runAutoAuditRules(ctx: AuditContext): AuditNoteEntry[] {
  const newEntries: AuditNoteEntry[] = [];
  const singleRules = [
    checkShortGroundTime,
    checkLongGroundTime,
    checkDocumentationDelay,
    checkCarrierCommsIssues,
  ];
  for (const rule of singleRules) {
    const entry = rule(ctx);
    if (entry) newEntries.push(entry);
  }
  newEntries.push(...checkBarrierDurations(ctx));
  return newEntries;
}
